import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronDown, FaRocket } from 'react-icons/fa';
import { usePageData } from '../hooks/useFirestoreData';
import '../styles/Home.css';

/*
  Firestore doc: pages/home
  {
    firstName: "Ashish",
    lastName: "Kumar",
    roles: ["Software Engineer", "Full-Stack Developer", ...],
    tagline: "Building modern web applications...",
    stats: [
      { value: "5+", label: "Years Exp" },
      { value: "30+", label: "Projects" },
      { value: "10+", label: "Technologies" }
    ]
  }
*/

const DEFAULT_HOME = {
  firstName: 'Ashish',
  lastName: 'Kumar',
  roles: ['Software Engineer', 'Full-Stack Developer', 'UI/UX Enthusiast', 'Creative Coder'],
  tagline: 'Building modern web applications with passion and precision.',
  stats: [
    { value: '5+', label: 'Years Exp' },
    { value: '30+', label: 'Projects' },
    { value: '10+', label: 'Technologies' },
  ],
};

/* Typing animation hook */
function useTypingEffect(texts, typingSpeed = 80, deletingSpeed = 40, pauseDuration = 2000) {
  const [displayText, setDisplayText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!texts || texts.length === 0) return;
    const currentText = texts[textIndex];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentText.slice(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
        if (charIndex + 1 === currentText.length) {
          setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      } else {
        setDisplayText(currentText.slice(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
        if (charIndex <= 1) {
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % texts.length);
        }
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, texts, typingSpeed, deletingSpeed, pauseDuration]);

  return displayText;
}

function Home() {
  const { data } = usePageData('home', DEFAULT_HOME);
  const typedText = useTypingEffect(data.roles);

  return (
    <section className="home">
      <div className="home-grid" />
      <div className="home-gradient-orb home-orb-1" />
      <div className="home-gradient-orb home-orb-2" />
      <div className="home-gradient-orb home-orb-3" />

      <div className="home-content">
        <motion.div
          className="terminal-badge"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <span className="terminal-dot" />
          <span className="terminal-text">system.initialize()</span>
        </motion.div>

        <motion.h1
          className="hero-name"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          <span className="name-line">{data.firstName}</span>
          <span className="name-line name-accent">{data.lastName}</span>
        </motion.h1>

        <motion.div
          className="typing-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <span className="typing-prefix">&gt;&gt; </span>
          <span className="typing-text">{typedText}</span>
          <span className="typing-cursor">|</span>
        </motion.div>

        <motion.p
          className="hero-tagline"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
        >
          {data.tagline}
        </motion.p>

        <motion.div
          className="hero-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
        >
          <Link to="/about" className="btn btn-primary">
            <FaRocket /> Explore My Work
          </Link>
          <Link to="/contact" className="btn btn-outline">
            Get In Touch
          </Link>
        </motion.div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          {data.stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="stat-divider" />}
              <div className="stat">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
              </div>
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <span>Scroll</span>
        <FaChevronDown className="scroll-arrow" />
      </motion.div>
    </section>
  );
}

export default Home;
